import { useMemo, useRef, useState } from 'react'
import { AlertCircle, Braces, Download, Eye, EyeOff, FileText, RefreshCw, UploadCloud } from 'lucide-react'
import type { DeterministicPreview, ProtocolSource, UUID } from '../../types'
import { DOCUMENT_ROLES, downloadText, formatBytes, formatDateTime, humanizeCode, pluralize, roleLabel, scopeLabel } from '../../utils/parser'
import ProtocolPreparationLoader from '../ProtocolPreparationLoader'
import { Badge, CopyButton, Metric, Notice, Section } from './StudyUi'

type SourceUpdate = { document_role?: string; is_active?: boolean }

type PackageSourceDoc = { protocol_id?: string; filename?: string; status?: string; protocol_scope?: string; coverage?: string[]; missing_expected_study_segments?: string[] }

export default function SourcesTab({
  sources, sourcesError, preview, preparing, preparingStartedAt, previewError, uploading, uploadProgress, canUpload, onUpload, onUpdateSource, onRetryPreview,
}: {
  sources: ProtocolSource[] | null
  sourcesError: string | null
  preview: DeterministicPreview | null
  preparing: boolean
  preparingStartedAt?: string | null
  previewError: string | null
  uploading: boolean
  uploadProgress: string | null
  canUpload: boolean
  onUpload: (files: File[]) => void
  onUpdateSource: (id: UUID, update: SourceUpdate) => Promise<void>
  onRetryPreview: () => void
}) {
  const packageDocs = useMemo(() => {
    const docs = (preview?.clin_spark_study_package?.source_documents || []) as PackageSourceDoc[]
    return new Map(docs.map((doc) => [String(doc.protocol_id), doc]))
  }, [preview])
  const sections = Array.isArray(preview?.sections) ? preview!.sections : []
  const activeCount = (sources || []).filter((source) => source.is_active).length

  return (
    <div className="space-y-8">
      <Section
        title="Protocol documents"
        description="Every PDF that feeds this study's build. Criteria, visits and ranges found in more than one document are merged and attributed to each source. Excluded documents keep their upload and can be re-included at any time."
      >
        {canUpload && <UploadDropzone uploading={uploading} progress={uploadProgress} onFiles={onUpload} />}
        {sourcesError && <Notice tone="red">{sourcesError}</Notice>}
        {sources === null && !sourcesError ? (
          <div className="space-y-2" aria-busy="true">{[0, 1].map((key) => <div key={key} className="h-16 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-800" />)}</div>
        ) : (sources || []).length === 0 ? (
          <Notice tone="blue">No protocol documents are linked to this study yet. Upload one or more protocol PDFs to start.</Notice>
        ) : (
          <ul className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200 bg-white dark:divide-gray-700 dark:border-gray-700 dark:bg-gray-800">
            {(sources || []).map((source) => (
              <SourceRow key={source.id} source={source} detected={packageDocs.get(String(source.protocol_id))} lastActive={source.is_active && activeCount <= 1} onUpdate={onUpdateSource} />
            ))}
          </ul>
        )}
      </Section>

      <Section
        title="Preparation"
        description="The pages and sections selected from each document before extraction. Extraction is deterministic; nothing is sent to an AI model unless AI extraction is enabled."
        actions={preview || previewError ? <button type="button" className="btn-secondary" onClick={onRetryPreview} disabled={preparing}><RefreshCw className={`mr-1.5 h-4 w-4 ${preparing ? 'animate-spin' : ''}`} aria-hidden={true} />Refresh</button> : undefined}
      >
        {preparing && <ProtocolPreparationLoader startedAt={preparingStartedAt} />}
        {previewError && !preparing && <Notice tone="red"><span className="flex items-start gap-2"><AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden={true} />{previewError}</span></Notice>}
        {!preparing && !preview && !previewError && <Notice tone="blue">Preparation results appear here after a protocol document is uploaded.</Notice>}
        {preview && !preparing && (
          <>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
              <Metric label="PDF pages" value={preview.total_pdf_pages} />
              <Metric label="Pages selected" value={preview.selected_page_count} />
              <Metric label="Sections" value={sections.length} />
              <Metric label="Words" value={(preview.counts?.word_count ?? 0).toLocaleString()} />
              <Metric label="Tokens (est.)" value={(preview.counts?.token_count ?? 0).toLocaleString()} hint={preview.tokenization?.note} />
            </div>
            {preview.clin_spark_study_package?.status === 'FAILED' && (
              <Notice tone="red">
                Extraction failed: {preview.clin_spark_study_package.reason}. Missing: {(preview.clin_spark_study_package.missing_entities || []).join(', ') || 'unknown'}
                {(preview.clin_spark_study_package.quality_issues || []).length > 0 && <div className="mt-1">Quality issues: {preview.clin_spark_study_package.quality_issues!.join('; ')}</div>}
              </Notice>
            )}
            <PreparedSections sections={sections} />
            <RawOutputs preview={preview} />
          </>
        )}
      </Section>
    </div>
  )
}

function UploadDropzone({ uploading, progress, onFiles }: { uploading: boolean; progress: string | null; onFiles: (files: File[]) => void }) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)
  const accept = (list: FileList | null) => {
    const files = Array.from(list || [])
    if (files.length) onFiles(files)
  }
  return (
    <div
      onDragOver={(event) => { event.preventDefault(); if (!uploading) setDragging(true) }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => { event.preventDefault(); setDragging(false); if (!uploading) accept(event.dataTransfer.files) }}
      className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-6 text-center transition-colors ${dragging ? 'border-primary-400 bg-primary-50 dark:bg-primary-950/30' : 'border-gray-300 bg-gray-50/60 dark:border-gray-600 dark:bg-gray-900/40'}`}
    >
      <UploadCloud className={`h-7 w-7 ${dragging ? 'text-primary-500' : 'text-gray-400'}`} aria-hidden={true} />
      {uploading ? (
        <p className="text-sm font-medium text-gray-700 dark:text-gray-200" role="status">{progress || 'Uploading…'}</p>
      ) : (
        <>
          <p className="text-sm text-gray-700 dark:text-gray-200">
            Drag protocol PDFs here, or{' '}
            <button type="button" className="font-medium text-primary-600 hover:underline dark:text-primary-400" onClick={() => inputRef.current?.click()}>browse</button>
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Add a screening, dosing or intervention-specific protocol to an existing study; shared content is merged automatically.</p>
        </>
      )}
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,application/pdf"
        multiple
        className="hidden"
        aria-label="Upload protocol documents"
        onChange={(event) => { accept(event.target.files); event.currentTarget.value = '' }}
      />
    </div>
  )
}

function SourceRow({ source, detected, lastActive, onUpdate }: {
  source: ProtocolSource
  detected?: PackageSourceDoc
  lastActive: boolean
  onUpdate: (id: UUID, update: SourceUpdate) => Promise<void>
}) {
  const [saving, setSaving] = useState<'role' | 'active' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const run = async (kind: 'role' | 'active', update: SourceUpdate) => {
    setSaving(kind)
    setError(null)
    try {
      await onUpdate(source.id, update)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The change could not be saved.')
    } finally {
      setSaving(null)
    }
  }
  const roleOptions = DOCUMENT_ROLES.some(([key]) => key === source.document_role) ? DOCUMENT_ROLES : [...DOCUMENT_ROLES, [source.document_role, roleLabel(source.document_role)] as const]
  const scope = detected?.protocol_scope
  return (
    <li className={`flex flex-col gap-3 px-4 py-3 md:flex-row md:items-center ${source.is_active ? '' : 'bg-gray-50 dark:bg-gray-900/40'}`}>
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <FileText className={`mt-0.5 h-5 w-5 shrink-0 ${source.is_active ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400'}`} aria-hidden={true} />
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`truncate font-medium ${source.is_active ? 'text-gray-900 dark:text-white' : 'text-gray-500 line-through decoration-gray-300'}`} title={source.filename || undefined}>{source.filename || 'Untitled document'}</span>
            {!source.is_active && <Badge>Excluded</Badge>}
            {scope && <Badge tone="blue" title="Detected from the document's content">Detected: {scopeLabel(scope)}</Badge>}
          </div>
          <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            {[source.page_count ? pluralize(source.page_count, 'page') : null, formatBytes(source.file_size_bytes), source.uploaded_at ? `uploaded ${formatDateTime(source.uploaded_at)}` : null].filter(Boolean).join(' · ')}
          </div>
          {detected?.coverage && detected.coverage.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">{detected.coverage.map((item) => <Badge key={item}>{humanizeCode(item)}</Badge>)}</div>
          )}
          {error && <p className="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">{error}</p>}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <label className="sr-only" htmlFor={`role-${source.id}`}>Document role for {source.filename}</label>
        <select
          id={`role-${source.id}`}
          value={source.document_role || 'unknown'}
          disabled={saving !== null}
          onChange={(event) => run('role', { document_role: event.target.value })}
          className="rounded-lg border border-gray-300 bg-white py-1.5 pl-2 pr-8 text-sm text-gray-700 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
        >
          {roleOptions.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
        </select>
        <button
          type="button"
          className="btn-secondary whitespace-nowrap"
          disabled={saving !== null || lastActive}
          title={lastActive ? 'A study needs at least one included document' : undefined}
          onClick={() => run('active', { is_active: !source.is_active })}
        >
          {source.is_active ? <EyeOff className="mr-1.5 h-4 w-4" aria-hidden={true} /> : <Eye className="mr-1.5 h-4 w-4" aria-hidden={true} />}
          {saving === 'active' ? 'Saving…' : source.is_active ? 'Exclude' : 'Include'}
        </button>
      </div>
    </li>
  )
}

function PreparedSections({ sections }: { sections: DeterministicPreview['sections'] }) {
  const groups = useMemo(() => {
    const map = new Map<string, DeterministicPreview['sections']>()
    for (const section of sections) {
      const key = section.source_filename || 'Protocol'
      map.set(key, [...(map.get(key) || []), section])
    }
    return [...map.entries()]
  }, [sections])
  if (!sections.length) return <Notice>No sections were selected from the documents.</Notice>
  return (
    <div className="space-y-3">
      <h4 className="font-semibold text-gray-900 dark:text-white">Prepared sections</h4>
      {groups.map(([filename, list]) => (
        <details key={filename} open={groups.length === 1} className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
          <summary className="flex cursor-pointer items-center justify-between gap-3 bg-gray-50 px-4 py-2 text-sm dark:bg-gray-800">
            <span className="flex min-w-0 items-center gap-2 font-medium text-gray-900 dark:text-white"><FileText className="h-4 w-4 shrink-0 text-gray-400" aria-hidden={true} /><span className="truncate">{filename}</span></span>
            <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">{pluralize(list.length, 'section')}{list[0]?.document_role && list[0].document_role !== 'unknown' ? ` · ${roleLabel(list[0].document_role)}` : ''}</span>
          </summary>
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
              <tr><th className="px-4 py-2 font-medium">Section</th><th className="px-4 py-2 font-medium">Topic</th><th className="px-4 py-2 font-medium">PDF pages</th><th className="px-4 py-2 text-right font-medium">Words</th></tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/70">
              {list.map((section, index) => (
                <tr key={`${section.topic}-${index}`}>
                  <td className="px-4 py-2 text-gray-900 dark:text-white">{section.toc_label || humanizeCode(section.topic)}</td>
                  <td className="px-4 py-2 text-gray-500 dark:text-gray-400">{humanizeCode(section.topic)}</td>
                  <td className="px-4 py-2 tabular-nums text-gray-600 dark:text-gray-300">{section.page_start === section.page_end ? section.page_start : `${section.page_start}–${section.page_end}`}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-gray-600 dark:text-gray-300">{(section.counts?.word_count ?? 0).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      ))}
    </div>
  )
}

/** Large raw outputs are only serialised when the user opens them. */
function RawOutputs({ preview }: { preview: DeterministicPreview }) {
  return (
    <div className="space-y-3">
      <h4 className="font-semibold text-gray-900 dark:text-white">Raw outputs</h4>
      <LazyText
        title="Prepared extraction payload"
        description="The exact text assembled from the selected sections."
        filename="prepared_payload.txt"
        mime="text/plain;charset=utf-8"
        produce={() => preview.ai_payload || ''}
      />
      {preview.clin_spark_study_package && (
        <LazyText
          title="Machine-readable study package"
          description="The full deterministic ClinSpark package (JSON) used by every tab on this page."
          filename="clinspark_study_package.json"
          mime="application/json"
          produce={() => JSON.stringify(preview.clin_spark_study_package, null, 2)}
        />
      )}
    </div>
  )
}

function LazyText({ title, description, filename, mime, produce }: { title: string; description: string; filename: string; mime: string; produce: () => string }) {
  const [open, setOpen] = useState(false)
  const text = useMemo(() => (open ? produce() : ''), [open, produce])
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2">
        <div className="flex min-w-0 items-center gap-2">
          <Braces className="h-4 w-4 shrink-0 text-gray-400" aria-hidden={true} />
          <div className="min-w-0">
            <div className="text-sm font-medium text-gray-900 dark:text-white">{title}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">{description}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <CopyButton text={produce} label="Copy" />
          <button type="button" className="btn-secondary" onClick={() => downloadText(filename, produce(), mime)}><Download className="mr-1.5 h-4 w-4" aria-hidden={true} />Download</button>
          <button type="button" className="btn-secondary" aria-expanded={open} onClick={() => setOpen((value) => !value)}>{open ? 'Hide' : 'View'}</button>
        </div>
      </div>
      {open && <textarea readOnly value={text} aria-label={title} className="block h-96 w-full resize-y border-t border-gray-200 bg-gray-50 p-3 font-mono text-xs text-gray-800 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200" />}
    </div>
  )
}

export type { SourceUpdate }
